import mitt from 'mitt'
import Modal from './Modal.vue'
import Dialog from './Dialog.vue'
import ModalsContainer from './ModalsContainer.vue'
import {
    createDivInBody
} from './utils'
import { createApp, h } from 'vue'

const defaultComponentName = 'Modal'

const UNMOUNTED_ROOT_ERROR_MESSAGE =
    '[vue-js-modal] ' +
    'In order to render dynamic modals, a <modals-container> ' +
    'component must be present on the page.'

const DYNAMIC_MODAL_DISABLED_ERROR =
    '[vue-js-modal] ' +
    '$modal() received object as a first argument, but dynamic modals are ' +
    'switched off. https://github.com/euvl/vue-js-modal/#dynamic-modals'

const UNSUPPORTED_ARGUMENT_ERROR =
    '[vue-js-modal] ' +
    '$modal() received an unsupported argument as a first argument.'

export const getModalsContainer = (app, options, root) => {
    if (!root._dynamicContainer && options.injectModalsContainer) {
        const container = createDivInBody()
        const modalApp = createApp({
            render() { return h(ModalsContainer) }
        })
        modalApp.mount(container)
    }

    return root._dynamicContainer
}

const Plugin = {
    install(app, options = {}) {
        /**
         * Makes sure that plugin can be installed only once
         */
        if (this.installed) {
            return
        }

        this.installed = true
        this.event = mitt()
        this.rootInstance = null

        const componentName = options.componentName || defaultComponentName
        const dynamicDefaults = options.dynamicDefaults || {}
        /**
         * Plugin API
         */
        const showStaticModal = (modal, params) => {
            Plugin.event.emit('toggle', { name: modal, state: true, params })
        }

        const showDynamicModal = (modal, props, params, events) => {
            const root = params && params.root ? params.root : Plugin.rootInstance
            const container = getModalsContainer(app, options, root)
            if (container) {
                container.add(modal, props, {
                    ...dynamicDefaults,
                    ...params
                }, events)
                return
            }

            console.warn(UNMOUNTED_ROOT_ERROR_MESSAGE)
        }
        const IGNORE_HISTORY_MODALS = new Set(['dialog'])
        // How long to wait for the popstate that history.back() owes us
        // before assuming it is not coming (a traversal the browser
        // dropped) and carrying on as if it had landed.
        const POP_GRACE_MS = 1000

        const ModalManager = {
            _stack: [],
            _queue: Promise.resolve(),
            _handlingPop: false,
            // history.back() calls issued by hide() whose popstate has not
            // landed yet, and the entries show() has since put on the
            // stack but not yet in history — see hide().
            _pendingBacks: 0,
            _pendingTimer: null,
            _deferredPushes: [],

            _run(fn) {
                // A show() that throws — a before-open handler, a rate-
                // limited pushState — must not poison the queue: every
                // later show() would chain onto the rejection and never
                // run, and the app would silently stop opening modals.
                this._queue = this._queue
                    .then(() => Promise.resolve().then(fn))
                    .catch((err) => { console.error('[vue-js-modal]', err) })
                return this._queue
            },

            _pushEntry(entry) {
                history.pushState({ ...history.state, overlay: entry.name, type: entry.type, ts: entry.ts }, '')
            },

            // Put an entry in history now — or, while a back() from a
            // hide() is still in flight, once it has landed. Pushing
            // straight away would put the entry on top of the one about
            // to be popped, and the pop would take the newcomer instead.
            _placeEntry(entry) {
                if (this._pendingBacks > 0) this._deferredPushes.push(entry)
                else this._pushEntry(entry)
            },

            show(name, ...args) {
                return this._run(() => {
                    if (!IGNORE_HISTORY_MODALS.has(name)) {
                        this._stack = this._stack.filter(e => e.name !== name)
                        this._deferredPushes = this._deferredPushes.filter(e => e.name !== name)
                        const entry = { type: 'modal', name, ts: Date.now() }
                        this._stack.push(entry)
                        this._placeEntry(entry)
                    }
                    showStaticModal(name, ...args)
                })
            },

            hide(name, params) {
                const top = this._stack[this._stack.length - 1]

                // Always remove from stack and emit close immediately.
                // If this modal was pushed via history.pushState, also
                // call history.back() — after cleanup, so the popstate it
                // causes finds the stack already settled.
                this._stack = this._stack.filter(entry => entry.name !== name)
                Plugin.event.emit('toggle', { name, state: false, params })

                // An entry still waiting to be pushed was never in
                // history: forget it, nothing to unwind.
                const deferred = this._deferredPushes.length
                this._deferredPushes = this._deferredPushes.filter(e => e.name !== name)
                if (deferred !== this._deferredPushes.length) return

                // Only unwind if history is still sitting on the entry
                // this modal pushed. The stack alone cannot answer that:
                // hide() runs synchronously while show() is queued, and
                // history.back() only takes effect on a later turn, so
                // the live state is what is checked. Worst case an entry
                // is left behind, which costs an extra Back press on a
                // URL that never changed; the alternative costs the page.
                const s = history.state
                const isOurs = !!(top && top.name === name && s &&
                    s.overlay === name && (top.ts === undefined || s.ts === top.ts))

                if (isOurs) this._back()
            },

            // history.back() with its popstate accounted for. The pop
            // lands asynchronously — after however long the main thread
            // is busy opening whatever replaces this modal — so it is
            // counted rather than timed: handlePopState() swallows one
            // pop per back() owed, whenever it arrives. A timed flag
            // (formerly 50 ms) let a slow render turn our own pop into a
            // Back press that closed the modal that had just opened: the
            // tree picker handing off to the scan importer closed it
            // again on a busy page.
            _back() {
                this._pendingBacks++
                history.back()
                clearTimeout(this._pendingTimer)
                this._pendingTimer = setTimeout(() => this._settlePendingBacks(), POP_GRACE_MS)
            },

            _settlePendingBacks() {
                clearTimeout(this._pendingTimer)
                this._pendingTimer = null
                this._pendingBacks = 0
                const pushes = this._deferredPushes
                this._deferredPushes = []
                pushes.forEach(entry => this._pushEntry(entry))
            },

            /**
             * The window's popstate, routed here by App.vue. Returns
             * true when the pop was one hide() asked for; a user's Back
             * closes the top of the stack instead.
             */
            handlePopState() {
                if (this._pendingBacks > 0) {
                    this._pendingBacks--
                    if (this._pendingBacks === 0) this._settlePendingBacks()
                    return true
                }
                if (this._handlingPop) return true
                this._handlingPop = true
                try {
                    this.closeTop()
                } finally {
                    setTimeout(() => { this._handlingPop = false }, 0)
                }
                return false
            },

            toggle(name, params) {
                Plugin.event.emit('toggle', { name, state: undefined, params })
            },

            register(type, name, closeFn) {
                return this._run(() => {
                    const top = this._stack[this._stack.length - 1]
                    if (top && top.name === name) return
                    const entry = { type, name, closeFn, ts: Date.now() }
                    this._stack.push(entry)
                    this._placeEntry(entry)
                })
            },

            closeTop() {
                const entry = this._stack.pop()
                if (!entry) return
                this._deferredPushes = this._deferredPushes.filter(e => e !== entry)

                if (entry.type === 'modal') {
                    Plugin.event.emit('toggle', { name: entry.name, state: false })
                } else if (entry.closeFn) {
                    entry.closeFn()
                }
            }
        }

        app.config.globalProperties.$modal = ModalManager

        /**
         * Sets custom component name (if provided)
         */
        app.component(componentName, Modal)
        /**
         * Registration of <Dialog/> component
         */
        if (options.dialog) {
            app.component('VDialog', Dialog)
        }
        /**
         * Registration of <ModalsContainer/> component
         */
        if (options.dynamic) {
            app.component('ModalsContainer', ModalsContainer)
            app.mixin({
                beforeMount() {
                    if (Plugin.rootInstance === null) {
                        Plugin.rootInstance = this.$root
                    }
                }
            })
        }
    }
}


export default Plugin
